import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { Plus, Search, MoreHorizontal, Eye, Edit, Trash2, Warehouse, MapPin, Package, TrendingUp, Activity } from 'lucide-react';
import WarehouseService from '@/api/warehouses.service';
import { canCreate, canView, canEdit, canDelete } from '@/lib/permissions';

interface Warehouse {
  publicId: string;
  name: string;
  code: string;
  description?: string;
  address?: string;
  contactPerson?: string;
  contactPhone?: string;
  contactEmail?: string;
  capacity?: number;
  timezone?: string;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

interface WarehouseStats {
  totalWarehouses: number;
  activeWarehouses: number;
  inactiveWarehouses: number;
  totalCapacity: number;
  utilizationPercentage: number;
  recentActivities: number;
}

const Warehouses: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [filteredWarehouses, setFilteredWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [stats, setStats] = useState<WarehouseStats>({
    totalWarehouses: 0,
    activeWarehouses: 0,
    inactiveWarehouses: 0,
    totalCapacity: 0,
    utilizationPercentage: 0,
    recentActivities: 0
  });

  const businessId = parseInt(localStorage.getItem('businessId') || '1');

  useEffect(() => {
    fetchWarehouses();
  }, []);

  useEffect(() => {
    filterWarehouses();
  }, [warehouses, searchTerm, statusFilter]);

  const fetchWarehouses = async () => {
    try {
      setLoading(true);
      const response = await WarehouseService.getAllWarehouses(businessId);
      
      if (response.data?.success) {
        const warehouseList = response.data?.warehouses || [];
        setWarehouses(warehouseList);
        calculateStats(warehouseList);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: response.data.message || "Failed to fetch warehouses",
        });
      }
    } catch (error) {
      console.error('Error fetching warehouses:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch warehouses",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (warehouseList: Warehouse[]) => {
    const totalWarehouses = warehouseList.length;
    const activeWarehouses = warehouseList.filter(w => w.status === 'active').length;
    const inactiveWarehouses = totalWarehouses - activeWarehouses;
    const totalCapacity = warehouseList.reduce((sum, w) => sum + (w.capacity || 0), 0);
    
    setStats({
      totalWarehouses,
      activeWarehouses,
      inactiveWarehouses,
      totalCapacity,
      utilizationPercentage: 75, // This would come from actual utilization data
      recentActivities: 24 // This would come from actual activity data
    });
  };

  const filterWarehouses = () => {
    let filtered = warehouses;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(warehouse =>
        warehouse.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        warehouse.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        warehouse.address?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(warehouse => warehouse.status === statusFilter);
    }

    setFilteredWarehouses(filtered);
  };

  const handleDeleteWarehouse = async (warehouse: Warehouse) => {
    if (!canDelete('Warehouse')) {
      toast({
        variant: "destructive",
        title: "Permission Denied",
        description: "You don't have permission to delete warehouses",
      });
      return;
    }

    if (window.confirm(`Are you sure you want to delete warehouse "${warehouse.name}"?`)) {
      try {
        await WarehouseService.deleteWarehouse(warehouse.publicId);
        toast({
          title: "Success",
          description: "Warehouse deleted successfully",
        });
        fetchWarehouses();
      } catch (error) {
        console.error('Error deleting warehouse:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to delete warehouse",
        });
      }
    }
  };

  const getStatusBadge = (status: string) => {
    return status === 'active' 
      ? <Badge variant="default" className="bg-green-100 text-green-800">Active</Badge>
      : <Badge variant="secondary" className="bg-red-100 text-red-800">Inactive</Badge>;
  };

  const formatCapacity = (capacity?: number) => {
    if (!capacity) return 'N/A';
    return capacity >= 1000 ? `${(capacity / 1000).toFixed(1)}K` : capacity.toString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Warehouse Management</h1>
          <p className="text-muted-foreground">
            Manage warehouses, locations, and storage facilities
          </p>
        </div>
        {canCreate('Warehouse') && (
          <Button onClick={() => navigate('/warehouses/create')} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Create Warehouse
          </Button>
        )}
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Warehouses</CardTitle>
            <Warehouse className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalWarehouses}</div>
            <p className="text-xs text-muted-foreground">
              {stats.activeWarehouses} active, {stats.inactiveWarehouses} inactive
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Capacity</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCapacity(stats.totalCapacity)}</div>
            <p className="text-xs text-muted-foreground">
              Storage capacity units
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Utilization</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.utilizationPercentage}%</div>
            <p className="text-xs text-muted-foreground">
              Average capacity utilization
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Recent Activities</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.recentActivities}</div>
            <p className="text-xs text-muted-foreground">
              Last 24 hours
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Search & Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search warehouses by name, code, or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Warehouses List */}
      <Card>
        <CardHeader>
          <CardTitle>
            Warehouses ({filteredWarehouses.length})
          </CardTitle>
          <CardDescription>
            Manage your warehouse facilities and storage locations
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredWarehouses.length === 0 ? (
            <div className="text-center py-8">
              <Warehouse className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No warehouses found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {warehouses.length === 0 
                  ? "Get started by creating your first warehouse."
                  : "Try adjusting your search or filter criteria."
                }
              </p>
              {canCreate('Warehouse') && warehouses.length === 0 && (
                <div className="mt-6">
                  <Button onClick={() => navigate('/warehouses/create')} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Warehouse
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredWarehouses.map((warehouse) => (
                <Card key={warehouse.publicId} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-lg">{warehouse.name}</CardTitle>
                        <CardDescription className="text-sm text-gray-500">
                          Code: {warehouse.code}
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(warehouse.status)}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canView('Warehouse') && (
                              <DropdownMenuItem onClick={() => navigate(`/warehouses/${warehouse.publicId}`)}>
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                            )}
                            {canEdit('Warehouse') && (
                              <DropdownMenuItem onClick={() => navigate(`/warehouses/${warehouse.publicId}/edit`)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                            )}
                            {canDelete('Warehouse') && (
                              <DropdownMenuItem 
                                onClick={() => handleDeleteWarehouse(warehouse)}
                                className="text-red-600"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-2">
                      {warehouse.description && (
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {warehouse.description}
                        </p>
                      )}
                      {warehouse.address && (
                        <div className="flex items-center text-sm text-gray-500">
                          <MapPin className="h-3 w-3 mr-1" />
                          <span className="truncate">{warehouse.address}</span>
                        </div>
                      )}
                      {warehouse.capacity && (
                        <div className="flex items-center text-sm text-gray-500">
                          <Package className="h-3 w-3 mr-1" />
                          <span>Capacity: {formatCapacity(warehouse.capacity)}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-2">
                        <span className="text-xs text-gray-400">
                          Updated {new Date(warehouse.updatedAt).toLocaleDateString()}
                        </span>
                        <div className="flex gap-1">
                          {canView('Warehouse') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/warehouses/${warehouse.publicId}`)}
                            >
                              View
                            </Button>
                          )}
                          {canEdit('Warehouse') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/warehouses/${warehouse.publicId}/edit`)}
                            >
                              Edit
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Warehouses; 